import { useState } from "react";
import { Wand2, Copy, Download, AlertTriangle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAdminAuth } from "@/lib/AdminAuthContext";
import { adminFetchJson } from "@/lib/adminFetch";
import AdminLayout from "./AdminLayout";

const SUGGESTED_THEMES = [
  "Glutatión y energía",
  "Inmunidad después de los 40",
  "Sueño profundo y recuperación",
  "Menopausia con calma",
  "Antioxidantes naturales",
  "Estrés crónico y cortisol",
];

function PostCard({ post, index }) {
  const [tab, setTab] = useState("es");

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  const downloadImage = () => {
    const a = document.createElement("a");
    a.href = post.image;
    a.download = `eliora-post-${index + 1}-${post.angle}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!post.ok) {
    return (
      <div className="rounded-2xl border border-[#bc6c25]/30 bg-[#bc6c25]/5 p-5 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-[#bc6c25] mt-0.5" />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#bc6c25] font-semibold">{post.angle_label}</p>
          <p className="text-sm text-[#1a2421] mt-1">Falló este post. Puedes reintentarlo o regenerar el lote.</p>
        </div>
      </div>
    );
  }

  const caption = tab === "es" ? post.caption_es : post.caption_en;
  const hashtagsStr = (post.hashtags || []).join(" ");

  return (
    <div data-testid={`batch-post-${index}`} className="rounded-2xl border border-[#2c4a3b]/10 bg-white overflow-hidden shadow-[0_2px_12px_-6px_rgba(26,36,33,0.15)]">
      <div className="relative">
        <img src={post.image} alt={post.angle_label} className="w-full aspect-square object-cover" />
        <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#1a2421]/80 backdrop-blur text-white text-[10px] uppercase tracking-[0.18em]">
          {index + 1} · {post.angle_label}
        </div>
        <button
          onClick={downloadImage}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/90 hover:bg-white shadow"
          title="Descargar imagen"
        >
          <Download className="w-4 h-4 text-[#1a2421]" />
        </button>
      </div>

      <div className="p-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#f0ebe1]">
            <TabsTrigger value="es" data-testid={`tab-es-${index}`}>Español</TabsTrigger>
            <TabsTrigger value="en" data-testid={`tab-en-${index}`}>English</TabsTrigger>
          </TabsList>
          <TabsContent value="es" className="mt-3">
            <pre className="text-[13px] leading-relaxed text-[#1a2421] whitespace-pre-wrap font-sans max-h-44 overflow-y-auto">{post.caption_es}</pre>
          </TabsContent>
          <TabsContent value="en" className="mt-3">
            <pre className="text-[13px] leading-relaxed text-[#1a2421] whitespace-pre-wrap font-sans max-h-44 overflow-y-auto">{post.caption_en}</pre>
          </TabsContent>
        </Tabs>

        <div className="mt-3 p-2 rounded-lg bg-[#f0ebe1] text-[11px] text-[#2c4a3b] max-h-20 overflow-y-auto">
          {hashtagsStr}
        </div>

        <div className="mt-3 flex gap-2">
          <Button
            onClick={() => copy(`${caption}\n\n${hashtagsStr}`)}
            size="sm"
            className="flex-1 bg-[#2c4a3b] hover:bg-[#1f3529]"
            data-testid={`copy-post-${index}`}
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" /> Copiar
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function ContentStudio() {
  useAdminAuth();
  const [theme, setTheme] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async (selectedTheme) => {
    const t = (selectedTheme || theme).trim();
    if (t.length < 2) {
      toast.error("Escribe un tema de al menos 2 caracteres");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const data = await adminFetchJson("/api/admin/content/batch-posts", {
        method: "POST",
        body: JSON.stringify({ theme: t, count: 7 }),
      });
      setResult(data);
      const okCount = (data.posts || []).filter((p) => p.ok).length;
      toast.success(`${okCount} de ${data.count} posts generados`);
    } catch (e) {
      console.error(e);
      toast.error(`No se pudo generar: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#bc6c25] font-semibold">Content studio</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#1a2421] mt-1">Generador de 7 posts</h1>
        <p className="text-sm text-[#5c6b61] mt-2 max-w-2xl">
          Escribe un tema y la IA genera 7 posts listos para Instagram, cada uno con un ángulo distinto: educativo,
          rompe-mitos, inspiracional, hábito, testimonio, ciencia y CTA.
        </p>
      </div>

      <div className="rounded-2xl border border-[#2c4a3b]/10 bg-white p-5 md:p-6">
        <div className="flex flex-col md:flex-row gap-3">
          <Input
            data-testid="batch-theme-input"
            placeholder="Ej: glutatión y energía, menopausia con calma..."
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            disabled={loading}
            className="flex-1"
          />
          <Button
            onClick={() => generate()}
            disabled={loading}
            data-testid="batch-generate-btn"
            className="bg-[#bc6c25] hover:bg-[#9c581e] text-white px-6"
          >
            {loading ? (
              <>
                <Sparkles className="w-4 h-4 mr-2 animate-pulse" /> Generando 7 posts…
              </>
            ) : (
              <>
                <Wand2 className="w-4 h-4 mr-2" /> Generar 7 posts
              </>
            )}
          </Button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#5c6b61] mr-1 self-center">Sugerencias:</span>
          {SUGGESTED_THEMES.map((s) => (
            <button
              key={s}
              onClick={() => { setTheme(s); generate(s); }}
              disabled={loading}
              className="text-xs px-3 py-1.5 rounded-full bg-[#f0ebe1] hover:bg-[#bc6c25]/10 text-[#1a2421] disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>

        {loading && (
          <div className="mt-6 text-sm text-[#5c6b61]">
            Tardará 30-90 segundos. Generando imágenes con Gemini 3 Image + captions con GPT-5.2 en paralelo…
          </div>
        )}
      </div>

      {result && (
        <div className="mt-10">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-semibold">Tema: {result.theme}</p>
          <h2 className="font-serif text-2xl text-[#1a2421] mt-1">Resultados ({result.count} posts)</h2>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(result.posts || []).map((p, i) => (
              <PostCard key={`${p.angle}-${i}`} post={p} index={i} />
            ))}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
